"use client";

import React, { useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import "swiper/css";
import BottomCurves from "../curves/bottomCurves";
import BannerFloatingArrow from "../uiDecor/bannerFloatingArrow";
import ProjectCard from "../card/projectCard";

export default function CardStack({ data }: { data: any }) {
    const swiperRef = useRef<SwiperType | null>(null);

    const navigate = (dir: "prev" | "next") => {
        const swiper = swiperRef.current;
        if (!swiper) return;
        swiper.autoplay.stop();
        const go = () =>
            dir === "next" ? swiper.slideNext(300) : swiper.slidePrev(300);
        if (swiper.animating) {
            swiper.animating = false;
            swiper.slideTo(swiper.activeIndex, 0, false);
            requestAnimationFrame(go);
        } else {
            go();
        }
    };

    if (
        data.componentName &&
        data.componentName.componentName === "PastProjects"
    ) {
        return (
            <section className="bg-rainbow pt-20 pb-[12vw] relative z-[1]">
                <BannerFloatingArrow
                    className="left-[2vw] top-0 z-[-1] md:top-10"
                    direction="left"
                    arrowtype="scroll-on"
                />
                <div className="md:mb-20 z-[2]">
                    <h3 className="text-2xl font-bold md:text-font-60 text-center mb-9 md:mx-20">
                        {data.heading}
                    </h3>
                </div>

                <div
                    className="w-screen relative left-1/2 -translate-x-1/2 overflow-hidden pb-8 md:pb-0 z-[1]"
                    onMouseEnter={() => swiperRef.current?.autoplay?.stop()}
                    onMouseLeave={() => swiperRef.current?.autoplay?.start()}
                >
                    <button
                        onClick={() => navigate("prev")}
                        className="absolute left-4 top-1/2 -translate-y-1/2 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/70 bg-white/50 text-[#1f2c43] backdrop-blur-md transition-all duration-300 hover:border-[#851bed] hover:bg-[#851bed] hover:text-white md:h-[3vw] md:w-[3vw]"
                        aria-label="Previous slide"
                    >
                        <ChevronLeftIcon className="h-5 w-5 md:h-[1.4vw] md:w-[1.4vw]" />
                    </button>
                    <button
                        onClick={() => navigate("next")}
                        className="absolute right-4 top-1/2 -translate-y-1/2 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/70 bg-white/50 text-[#1f2c43] backdrop-blur-md transition-all duration-300 hover:border-[#851bed] hover:bg-[#851bed] hover:text-white md:h-[3vw] md:w-[3vw]"
                        aria-label="Next slide"
                    >
                        <ChevronRightIcon className="h-5 w-5 md:h-[1.4vw] md:w-[1.4vw]" />
                    </button>
                    <Swiper
                        onSwiper={(swiper) => {
                            swiperRef.current = swiper;
                        }}
                        modules={[Autoplay]}
                        slidesPerView={"auto"}
                        spaceBetween={24}
                        breakpoints={{
                            425: {
                                spaceBetween: 30,
                            },
                        }}
                        loop={true}
                        loopAdditionalSlides={data.projects.length}
                        centeredSlides={true}
                        speed={5000}
                        autoplay={{
                            delay: 0,
                            disableOnInteraction: false,
                            stopOnLastSlide: false,
                            waitForTransition: true,
                            pauseOnMouseEnter: true,
                        }}
                        allowTouchMove={true}
                        onTouchStart={(swiper) => {
                            swiper.params.speed = 300;
                        }}
                        onTransitionEnd={(swiper) => {
                            swiper.params.speed = 5000;
                        }}
                    >
                        {[
                            ...data.projects,

                            ...data.projects,

                            ...data.projects,
                            ,
                        ].map((project: any, index: number) => (
                            <SwiperSlide
                                key={index}
                                className="!w-auto !bg-transparent !text-left"
                            >
                                <ProjectCard data={project} index={index} />
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>

                <div className="relative z-10 -translate-y-[30px] md:translate-y-[20px] lg:translate-y-[50px] flex flex-col justify-center items-center">
                    <a
                        href="/works"
                        className="text-white bg-purple flex text-font-24 leading-[1] font-medium px-6 py-3 duration-200 border-[1px] border-purple rounded-full h-fit w-max whitespace-nowrap cursor-pointer hover:border-[#00fe8a] hover:bg-[#00fe8a] hover:text-purple"
                    >
                        Explore All Case Studies
                    </a>
                </div>

                <BottomCurves
                    color={data.waveBgColor.waveBgColor}
                    direction="right"
                />
            </section>
        );
    }

    return null;
}
